import { select, call, takeLatest, put } from 'redux-saga/effects';

import { AdminWorkActions } from '../constants';
import { AdminWorkApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteAdminWorkAction,
    GetAdminWorkAction,
    GetAdminWorksAction,
    PersistAdminWorkAction,
    getAdminWorksCsv,
    getAdminWorks,
} from '../actions/adminWork-actions';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { groupAdminWork } from '../../helpers/adminWork/adminWork';
import { groupDataToCsv } from '../../helpers/adminWork/groupCsv';
import { exportToCsv } from '../../services/exports/csv';
import sortingService from 'src/services/sorting';
import { toast } from 'react-toastify';
import _ from 'lodash';

function* handleFetchAdminWorks(action: GetAdminWorksAction) {
    const oldData = yield select((state: StoreState) => state.adminWorks.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    let filterProps: any = { ...filters };

    if (filterProps?.adminWorkDate) {
        filterProps = {
            ...filters,
            ...filterProps?.adminWorkDate,
        };
        delete filterProps.adminWorkDate;
    }
    let sortObj = { ...sorting };
    if (_.isEmpty(sortObj)) {
        sortObj = { adminWorkDate: 'desc' };
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        AdminWorkApi.getAdminWorks({
            ...filterProps,
            ...sortingService.makeOrderSymfony(sortObj),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchAdminWorksCsv(action: GetAdminWorksAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;
    let filterProps: any = { ...filters };

    if (filterProps?.adminWorkDate) {
        filterProps = {
            ...filters,
            ...filterProps?.adminWorkDate,
        };
        delete filterProps.adminWorkDate;
    }
    let sortObj = { ...sorting };
    if (_.isEmpty(sortObj)) {
        sortObj = { adminWorkDate: 'desc' };
    }
    yield resolveApiCall(
        action,
        () =>
            AdminWorkApi.getAdminWorks({
                ...filterProps,
                ...sortingService.makeOrderSymfony(sortObj),
                page,
                itemsPerPage: 5000,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                yield put(
                    getAdminWorksCsv({
                        filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                        all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                    }),
                );
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupDataToCsv(dataAll);
                    exportToCsv('admin_works.csv', groupCsv);
                } else {
                    const groupCsvData = groupDataToCsv(data.payload);
                    exportToCsv('admin_works.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchAdminWork(action: GetAdminWorkAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => AdminWorkApi.getAdminWork(id));
}

function* handleDeleteAdminWork(action: DeleteAdminWorkAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => AdminWorkApi.deleteAdminWork(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistAdminWork(action: PersistAdminWorkAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;
    const dataAdminWork = groupAdminWork(restData);
    yield resolveApiCall(
        action,
        () => (id ? AdminWorkApi.updateAdminWork(id, dataAdminWork) : AdminWorkApi.createAdminWork(dataAdminWork)),
        function* (data: ResolverApiSuccess) {
            toast.success(`Admin work was successfully ${!id ? 'created' : 'updated'}.`);
            yield call(callbacks.resolve);
            if (restData.month) {
                yield put(
                    getAdminWorks({
                        filters: {
                            'provider.id': restData?.provider,
                            'createdAt[before]': restData.month.monthEnd,
                            'createdAt[after]': restData.month.monthStart,
                        },
                        itemsPerPage: 1200,
                        page: 1,
                    }),
                );
            } else {
                //if (!id) {    }
                yield put(push(Router.generate(routes.ADMIN_WORKS_LIST)));
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (result.error && result.error.response && result.error.response.status) {
                    toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(AdminWorkActions.FETCH_ADMIN_WORKS, handleFetchAdminWorks);
    yield takeLatest(AdminWorkActions.FETCH_ADMIN_WORK, handleFetchAdminWork);
    yield takeLatest(AdminWorkActions.DELETE_ADMIN_WORK, handleDeleteAdminWork);
    yield takeLatest(AdminWorkActions.PERSIST_ADMIN_WORK, handlePersistAdminWork);
    yield takeLatest(AdminWorkActions.FETCH_ADMIN_WORKS_CSV, handleFetchAdminWorksCsv);
}
